import "./GradeResult.css";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import ReactMarkdown from "react-markdown";

interface GradeResultProps {
    /** 0–100 */
    grade: number;
    notes: string;
    /** Optional — renders a continue button if provided */
    onContinue?: () => void;
    continueLabel?: string;
    /** Optional — renders a button above Continue that hides the grade to reveal the question and answer underneath */
    onReviewAnswer?: () => void;
}

const LOW_COLOR = { r: 166, g: 72, b: 63 }; // muted brick red, grade near 0
const HIGH_COLOR = { r: 92, g: 122, b: 82 }; // muted moss green, grade near 100

function gradeToColor(grade: number): string {
    const t = Math.max(0, Math.min(100, grade)) / 100;
    const r = Math.round(LOW_COLOR.r + (HIGH_COLOR.r - LOW_COLOR.r) * t);
    const g = Math.round(LOW_COLOR.g + (HIGH_COLOR.g - LOW_COLOR.g) * t);
    const b = Math.round(LOW_COLOR.b + (HIGH_COLOR.b - LOW_COLOR.b) * t);
    return `rgb(${r}, ${g}, ${b})`;
}

function gradeToLabel(grade: number): string {
    if (grade >= 90) return "Excellent";
    if (grade >= 75) return "Solid";
    if (grade >= 50) return "Getting there";
    return "Needs work";
}

export default function GradeResult({ grade, notes, onContinue, continueLabel = "Continue", onReviewAnswer }: GradeResultProps) {
    const clamped = Math.max(0, Math.min(100, grade));
    const radius = 88;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (clamped / 100) * circumference;

    return (
        <div className="grade-result" style={{ backgroundColor: gradeToColor(clamped) }}>
            <div className="grade-result__content">
                <button
                    className="exit-button"
                    onClick={onContinue}
                    aria-label="Exit problem"
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path
                            d="M6 6 L18 18 M18 6 L6 18"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
                <div className="grade-result__wheel-panel">
                    <span className="eyebrow grade-result__eyebrow">Score</span>
                    <div className="grade-result__wheel">
                        <svg viewBox="0 0 200 200" width="220" height="220">
                            <circle className="grade-result__track" cx="100" cy="100" r={radius} strokeWidth="14" fill="none" />
                            <circle
                                className="grade-result__fill"
                                cx="100"
                                cy="100"
                                r={radius}
                                strokeWidth="14"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                strokeDashoffset={offset}
                                transform="rotate(-90 100 100)"
                            />
                        </svg>
                        <div className="grade-result__number">
                            <span className="grade-result__value">{Math.round(clamped)}</span>
                            <span className="grade-result__percent">%</span>
                        </div>
                    </div>
                    <span className="grade-result__qualifier">{gradeToLabel(clamped)}</span>
                </div>

                <div className="grade-result__notes-panel">
                    <span className="eyebrow grade-result__eyebrow">Feedback</span>
                    <div className="grade-result__notes">
                        <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {notes}
                        </ReactMarkdown>
                    </div>
                    <div className="grade-result__actions">
                        {onReviewAnswer && (
                            <button type="button" className="grade-result__review" onClick={onReviewAnswer}>
                                Show problem &amp; answer
                            </button>
                        )}

                        {onContinue && (
                            <button type="button" className="grade-result__continue" onClick={onContinue}>
                                {continueLabel}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}